import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { AxiosError } from "axios";

import { Button } from "@/marketing/components/ui/button";
import { Input } from "@/marketing/components/ui/input";
import { cn } from "@/marketing/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/marketing/components/ui/dialog";
import { communityAdminAPIClient } from "@/marketing/infrastructure/influencer/community/communityAdminAPIClient";
import { COMMUNITY_KEYS } from "@/marketing/infrastructure/influencer/community/utils/keys";
import { COMMUNITY_QUERIES } from "@/marketing/infrastructure/influencer/community/utils/queries";

interface SeedPostDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Admin cold-start seeding: publish a HELP post under an existing creator's identity.
export function SeedPostDialog({ open, onOpenChange }: SeedPostDialogProps) {
  const queryClient = useQueryClient();
  const [authorId, setAuthorId] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  // Only active categories are assignable (matches the backend, which rejects unknown/inactive ids).
  const { data: allCategories = [] } = useQuery({ ...COMMUNITY_QUERIES.categories(), enabled: open });
  const activeCategories = allCategories.filter((category) => category.isActive);

  useEffect(() => {
    if (!open) return;
    setAuthorId("");
    setTitle("");
    setBody("");
    setCategoryIds([]);
    setImageUrls([]);
    setUploading(false);
  }, [open]);

  const toggleCategory = (categoryId: string) =>
    setCategoryIds((current) =>
      current.includes(categoryId) ? current.filter((id) => id !== categoryId) : [...current, categoryId],
    );

  const authorIdNum = Number(authorId);
  const authorIdValid = Number.isInteger(authorIdNum) && authorIdNum > 0;

  const handlePickImages = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (!authorIdValid) {
      toast.error("Enter the author user ID before uploading images");
      return;
    }
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const url = await communityAdminAPIClient.uploadPostImage(authorIdNum, file);
        setImageUrls((prev) => [...prev, url]);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const mutation = useMutation({
    mutationFn: () =>
      communityAdminAPIClient.createSeedPost({
        authorId: Number(authorId),
        body: body.trim(),
        title: title.trim(),
        ...(categoryIds.length > 0 && { categoryIds }),
        ...(imageUrls.length > 0 && { imageUrls }),
      }),
    onSuccess: () => {
      toast.success("Seed post published");
      queryClient.invalidateQueries({ queryKey: COMMUNITY_KEYS.all });
      onOpenChange(false);
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error.response?.data?.message ?? "Failed to publish post");
    },
  });

  const canSubmit =
    authorIdValid &&
    title.trim() !== "" &&
    body.trim() !== "" &&
    !uploading &&
    !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tw-max-w-xl">
        <DialogHeader>
          <DialogTitle>Create post</DialogTitle>
        </DialogHeader>

        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-4 tw-px-1">
          <div className="tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-xs tw-text-amber-700">
            Post on behalf of a creator to seed the board. The post will appear under
            the creator&apos;s name, not Skin Seoul. Use for cold-start seeding only.
          </div>

          <div>
            <label className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-gray-700">
              Author (creator user ID) <span className="tw-text-red-600">*</span>
            </label>
            <Input
              value={authorId}
              onChange={(e) => setAuthorId(e.target.value)}
              inputMode="numeric"
              placeholder="e.g. 1024"
            />
          </div>

          <div>
            <label className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-gray-700">
              Title <span className="tw-text-red-600">*</span>
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="How long after Shipped did your product arrive?"
            />
          </div>

          <div>
            <label className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-gray-700">
              Body <span className="tw-text-red-600">*</span>
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write the post body as the creator would..."
              className="tw-min-h-[96px] tw-w-full tw-rounded-lg tw-border tw-border-gray-200 tw-px-3 tw-py-2 tw-text-sm tw-outline-none focus:tw-border-violet-500"
            />
          </div>

          {activeCategories.length > 0 && (
            <div>
              <label className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-gray-700">
                Categories <span className="tw-font-normal tw-text-gray-400">(optional)</span>
              </label>
              {/* One row that scrolls right past the edge instead of wrapping. */}
              <div className="tw-flex tw-gap-2 tw-overflow-x-auto tw-pb-1">
                {activeCategories.map((category) => (
                  <button
                    key={category.categoryId}
                    type="button"
                    onClick={() => toggleCategory(category.categoryId)}
                    className={cn(
                      "tw-shrink-0 tw-rounded-full tw-border tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium",
                      categoryIds.includes(category.categoryId)
                        ? "tw-border-gray-900 tw-bg-gray-900 tw-text-white"
                        : "tw-border-gray-200 tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50",
                    )}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-gray-700">Images</label>
            {imageUrls.length > 0 && (
              <div className="tw-mb-2 tw-grid tw-grid-cols-4 tw-gap-2">
                {imageUrls.map((url, idx) => (
                  <div key={url} className="tw-relative">
                    <img src={url} alt={`Uploaded image ${idx + 1}`} className="tw-aspect-square tw-w-full tw-rounded-lg tw-object-cover" />
                    <button
                      type="button"
                      onClick={() => setImageUrls((prev) => prev.filter((u) => u !== url))}
                      className="tw-absolute -tw-right-1.5 -tw-top-1.5 tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded-full tw-bg-gray-900 tw-text-xs tw-text-white"
                      aria-label="Remove image"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              disabled={uploading}
              onChange={(e) => {
                void handlePickImages(e.target.files);
                e.target.value = "";
              }}
              className="tw-block tw-w-full tw-text-xs tw-text-gray-600 file:mr-3 file:tw-rounded-md file:tw-border file:tw-border-gray-200 file:tw-bg-white file:tw-px-3 file:tw-py-1.5 file:tw-text-xs file:tw-font-medium hover:file:tw-bg-gray-50"
            />
            <p className="tw-mt-1 tw-text-[11px] tw-text-gray-400">
              {uploading ? "Uploading…" : "PNG/JPG/WEBP, up to 1 MB each. Enter the author ID first."}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!canSubmit}>
            {mutation.isPending ? "Publishing..." : "Publish post"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
